/**
 * Video hero by Notch — play/pause for HYLA product hero layout.
 * Scoped to [data-video-hero-notch]; does not alter shared video-section behavior.
 */
(function () {
  const SECTION_ATTR = '[data-video-hero-notch][data-vhn-play-control="true"]';
  const LABEL_PAUSE = 'Pause background video';
  const LABEL_PLAY = 'Play background video';

  function closestRoot(node) {
    return node && node.closest ? node.closest(SECTION_ATTR) : null;
  }

  function getMedia(root) {
    const section = root.querySelector('video-section[data-video-hero]');
    if (!section) return { kind: 'none' };

    const type = section.dataset.type || '';
    if (type === 'mp4') {
      const video = section.querySelector('video');
      return video ? { kind: 'mp4', section, video } : { kind: 'none' };
    }
    if (type === 'youtube' || type === 'vimeo') {
      return { kind: type, section };
    }
    return { kind: 'none' };
  }

  function setButtonState(button, playing) {
    if (!button) return;
    button.setAttribute('aria-pressed', playing ? 'true' : 'false');
    button.setAttribute('aria-label', playing ? LABEL_PAUSE : LABEL_PLAY);
    const pauseIcon = button.querySelector('.video-hero-notch__play-control-icon--pause');
    const playIcon = button.querySelector('.video-hero-notch__play-control-icon--play');
    if (pauseIcon) pauseIcon.hidden = !playing;
    if (playIcon) playIcon.hidden = playing;
  }

  function postYouTube(iframe, func) {
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: 'command', func, args: [] }),
      '*'
    );
  }

  function postVimeo(iframe, method) {
    if (!iframe || !iframe.contentWindow) return;
    iframe.contentWindow.postMessage(JSON.stringify({ method }), '*');
  }

  function waitForIframe(section, timeoutMs) {
    return new Promise((resolve) => {
      const existing = section.querySelector('iframe');
      if (existing) {
        resolve(existing);
        return;
      }
      const start = Date.now();
      const obs = new MutationObserver(() => {
        const iframe = section.querySelector('iframe');
        if (iframe) {
          obs.disconnect();
          resolve(iframe);
        } else if (Date.now() - start > timeoutMs) {
          obs.disconnect();
          resolve(null);
        }
      });
      obs.observe(section, { childList: true, subtree: true });
      setTimeout(() => {
        obs.disconnect();
        resolve(section.querySelector('iframe'));
      }, timeoutMs);
    });
  }

  function bindMp4(root, button, video) {
    const sync = () => setButtonState(button, !video.paused && !video.ended);
    video.addEventListener('play', sync);
    video.addEventListener('playing', sync);
    video.addEventListener('pause', sync);
    video.addEventListener('ended', sync);
    sync();

    button.addEventListener('click', () => {
      if (video.paused || video.ended) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  async function bindYouTube(root, button, section) {
    const iframe = await waitForIframe(section, 8000);
    if (!iframe) {
      button.classList.add('is-unavailable');
      return;
    }

    // Enable JS API on existing iframe if missing
    try {
      const url = new URL(iframe.src);
      if (url.searchParams.get('enablejsapi') !== '1') {
        url.searchParams.set('enablejsapi', '1');
        iframe.src = url.toString();
      }
    } catch (e) {
      /* ignore */
    }

    let playing = true;
    setButtonState(button, true);

    button.addEventListener('click', () => {
      if (playing) {
        postYouTube(iframe, 'pauseVideo');
        playing = false;
      } else {
        postYouTube(iframe, 'playVideo');
        playing = true;
      }
      setButtonState(button, playing);
    });

    window.addEventListener('message', (event) => {
      if (!event.origin.includes('youtube.com')) return;
      let data = event.data;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
      } catch (e) {
        return;
      }
      if (!data || data.event !== 'onStateChange') return;
      // 1 playing, 2 paused, 0 ended
      if (data.info === 1) {
        playing = true;
        setButtonState(button, true);
      } else if (data.info === 2 || data.info === 0) {
        playing = false;
        setButtonState(button, false);
      }
    });
  }

  async function bindVimeo(root, button, section) {
    const iframe = await waitForIframe(section, 8000);
    if (!iframe) {
      button.classList.add('is-unavailable');
      return;
    }

    let playing = true;
    setButtonState(button, true);

    button.addEventListener('click', () => {
      if (playing) {
        postVimeo(iframe, 'pause');
        playing = false;
      } else {
        postVimeo(iframe, 'play');
        playing = true;
      }
      setButtonState(button, playing);
    });

    window.addEventListener('message', (event) => {
      if (!event.origin.includes('vimeo.com')) return;
      let data = event.data;
      try {
        if (typeof data === 'string') data = JSON.parse(data);
      } catch (e) {
        return;
      }
      if (!data || !data.event) return;
      if (data.event === 'play') {
        playing = true;
        setButtonState(button, true);
      } else if (data.event === 'pause' || data.event === 'ended') {
        playing = false;
        setButtonState(button, false);
      }
    });

    // Ask Vimeo for events
    try {
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'play' }), '*');
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'pause' }), '*');
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'addEventListener', value: 'ended' }), '*');
    } catch (e) {
      /* ignore */
    }
  }

  function initRoot(root) {
    if (!root || root.dataset.vhnBound === 'true') return;
    root.dataset.vhnBound = 'true';

    const button = root.querySelector('[data-vhn-play-toggle]');
    if (!button) return;

    const media = getMedia(root);
    if (media.kind === 'none') {
      button.classList.add('is-unavailable');
      return;
    }

    if (media.kind === 'mp4') {
      bindMp4(root, button, media.video);
      return;
    }
    if (media.kind === 'youtube') {
      bindYouTube(root, button, media.section);
      return;
    }
    if (media.kind === 'vimeo') {
      bindVimeo(root, button, media.section);
    }
  }

  function initAll() {
    document.querySelectorAll(SECTION_ATTR).forEach(initRoot);
  }

  function onSectionLoad(event) {
    const root = event.target.querySelector
      ? event.target.querySelector(SECTION_ATTR) ||
        (event.target.matches?.(SECTION_ATTR) ? event.target : null)
      : null;
    if (root) {
      delete root.dataset.vhnBound;
      initRoot(root);
    } else {
      initAll();
    }
  }

  document.addEventListener('shopify:section:load', onSectionLoad);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
